import { okOrThrow } from 'packages/lib/api/annotations/rest';
import { ZodType, ZodTypeDef } from 'zod';
import { getBaseURLForAPIBackend, APIBackend } from 'packages/lib/utils/fetch';
import { getTokenSilently } from 'packages/lib/auth';

export const apiFetch =
  (api: APIBackend, commonHeaders?: HeadersInit) =>
  async (
    endpoint: string,
    method?: string,
    body?: BodyInit,
    headers?: HeadersInit,
    showErrorToast?: boolean,
    handleRejection?: (resp: Response) => Promise<Response>
  ): Promise<Response> => {
    const defaultHeaders = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${(await getTokenSilently())?.accessToken}`,
      },
    };
    return fetch(`${getBaseURLForAPIBackend(api)}${endpoint}`, {
      method,
      body,
      headers: {
        ...defaultHeaders.headers,
        ...(!commonHeaders ? undefined : commonHeaders),
        ...(!headers ? undefined : headers),
      },
    })
      .then((resp) => okOrThrow(resp, showErrorToast, handleRejection))
      .catch((error) => {
        // Check if it's a network error (e.g., failed to fetch, DNS error, VPN disconnected)
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          // This is a generic network error. Assume it could be VPN related for now.
          // The assumption in todo.md is that we can differentiate. For now, this is a reasonable heuristic.
          throw new Error(
            'Network error. Please check your internet connection and VPN/Global Protect status.'
          );
        }
        throw error; // Re-throw other errors
      });
  };

/**
 * Polaris API Fetcher
 */
export const polarisApiFetch = apiFetch('polarisHost');

export const polarisApiGet = async <T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  zodParse?: ZodType<T, Z>['parse'],
  showErrorToast?: boolean,
  overrideHeaders?: HeadersInit
): Promise<T | null> => {
  const resp = await polarisApiFetch(
    endpoint,
    undefined,
    undefined,
    overrideHeaders,
    showErrorToast
  );

  if (resp.status === 204) {
    return null;
  }

  const json = await resp.json();

  if (!zodParse) return json as T; // Explicitly cast to T if no zodParse is provided

  try {
    return zodParse(json);
  } catch (e) {
    console.error('Zod Failed to parse data for endpoint: ', endpoint);
    console.error('  JSON that failed parsing: ', json);
    console.error((e as Error).message);
    return null; // Return null if parsing fails
  }
};

export const polarisApiPost = async <U, T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  postData: U,
  zodParse: ZodType<T, Z>['parse'],
  showErrorToast?: boolean
): Promise<T | null> => {
  const body = JSON.stringify(postData);
  const data = await polarisApiFetch(
    endpoint,
    'POST',
    body,
    undefined,
    showErrorToast
  ).then((r) => {
    if (r.status === 204) {
      console.log(endpoint + ' status 204 (no content).');
      return null;
    }
    return r.json();
  });

  if (!data) {
    return null;
  }

  try {
    return zodParse(data);
  } catch (e) {
    console.error('Zod Failed to parse data for endpoint: ', endpoint);
    console.error('  JSON that failed parsing: ', data);
    console.error((e as Error).message);
    return null; // Return null if parsing fails
  }
};
